export const ORG_CAPABILITY_AVAILABILITY = Object.freeze({
    ORG_VIEW: 'LIVE',
    ORG_PROFILE_MANAGE: 'LIVE',
    ORG_MEMBER_MANAGE: 'LIVE',
    ORG_ROLE_MANAGE: 'LIVE',
    ORG_VERIFICATION_SUBMIT: 'LIVE',
    EVENT_MANAGE: 'LIVE',
    TICKETING_MANAGE: 'LIVE',
    // Audited at the 10A restart against enforcement rather than against the
    // schedule. Each note records what was looked for and not found, so the
    // next audit checks a claim instead of re-deriving one.
    //
    // No route reaches admission: the cloud endpoint the scanner client names,
    // `/api/v1/scanner/admissions`, is not registered, and production scanner
    // composition installs the GateMesh venue authority rather than the cloud
    // client. Admission is Edge authority, which is a device's, not a member's.
    //
    // Re-audited at the direct-cloud checkpoint and now settled by a release
    // ruling rather than left as an omission: **direct-cloud admission is not an
    // R1 requirement**, and GateMesh is the shipped R1 scanner admission
    // authority path. ADR 0030 is not withdrawn — it stays the rule for how the
    // lane must behave if it is ever built — but its production channel is
    // deferred, and this entry stays `PLANNED` deliberately rather than
    // pending. `docs/contracts/direct-cloud-admission.md` records the three
    // capabilities required before composition may ship.
    GATE_OPERATE: 'PLANNED', // No route consumes it.
    // Audited at the R1 admission tranche. `scanner-routes.ts` requires it on
    // four routes through `requireOrg`: creating a scanner activation, listing
    // an event's handsets, revoking one, and moving one to a gate. Supervising
    // a gate now includes deciding which physical devices may admit through it.
    //
    // Not `EVENT_MANAGE`: creating a gate is event setup, while trusting a
    // handset and withdrawing that trust from one somebody walked off with is
    // supervision, and putting it under the broader capability would let
    // everyone who can edit an event also trust a device.
    GATE_SUPERVISE: 'LIVE',
    // Audited at the R1 finance tranche against enforcement, not the schedule.
    // `finance-routes.ts` requires FINANCE_VIEW to raise a refund request and to
    // read a refund or the settlement statement, and FINANCE_APPROVE to decide
    // one, to execute the provider refund and to requery it. Six routes reach
    // them, and each calls `requireOrg` with the capability named here.
    FINANCE_VIEW: 'LIVE',
    FINANCE_APPROVE: 'LIVE',
    SUPPORT_ACT: 'PLANNED', // No route consumes it.
});
export const PLATFORM_CAPABILITY_AVAILABILITY = Object.freeze({
    OPS_ORGANIZER_ONBOARDING: 'LIVE',
    // The payment-exception queue landed in Slice 4 and is honoured by a route.
    OPS_SUPPORT: 'LIVE',
    // Audited with the organization capabilities above. Neither appears in any
    // route, service or surface — only in the vocabulary and in this register.
    OPS_TRUST_SAFETY: 'PLANNED', // No route consumes it.
    // `/api/v1/ops/finance/reconciliation` requires it, and the console has a
    // surface that reads it. Read-only on both sides: there is no companion
    // write, because every remedy it might tempt somebody into either does not
    // exist or belongs to an authority nobody has decided on.
    OPS_FINANCE_RECONCILIATION: 'LIVE',
    OPS_SECURITY: 'LIVE',
    OPS_PLATFORM_ADMIN: 'LIVE',
});
export function isOrgCapabilityLive(capability) {
    return ORG_CAPABILITY_AVAILABILITY[capability] === 'LIVE';
}
export function isPlatformCapabilityLive(capability) {
    return PLATFORM_CAPABILITY_AVAILABILITY[capability] === 'LIVE';
}
/**
 * What a capability an organizer holds would let them do, for the capabilities
 * the product has not built yet.
 *
 * A surface shows these so a held-but-unbuilt capability reads as a stated
 * absence rather than a greyed-out button with no explanation.
 *
 * These strings name the *power*, never the release that carries it. An earlier
 * version promised a slice number — "Scanner admission arrives in Slice 5" —
 * and those promises outlived their slices and went on being shown to
 * organizers long after Slice 5 had passed. Internal sequencing is not a
 * commitment the product can keep on a customer's screen, and a schedule is not
 * something this module can observe. Describe the capability; say nothing about
 * when.
 *
 * A capability is listed here only while it is `PLANNED`. Moving one to `LIVE`
 * means removing its entry in the same commit, and a test holds the two in step.
 */
export const PLANNED_CAPABILITY_NOTE = Object.freeze({
    GATE_OPERATE: 'Scanning attendees in at a gate.',
    SUPPORT_ACT: 'Helping attendees with their orders and tickets.',
});
//# sourceMappingURL=release.js.map